import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, MapPin, CreditCard, FileCheck2, Languages, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';



/**
 * TRUST SECTION.
 *
 * Every claim here is something a reviewer can independently verify today:
 * the street address resolves, security.txt is fetchable, homeowner contact
 * data really is withheld until purchase, payments really are processed by a
 * PCI-compliant processor.
 *
 * There is intentionally NO star rating, NO "rated 4.9 by thousands" line and
 * NO AggregateRating schema, because no review system exists yet. Publishing an
 * unearned rating is deceptive, violates Google's structured-data policy, and
 * is precisely what an enterprise or school-district procurement review would
 * ask us to substantiate. Trust is built from verifiable facts, not stars.
 */
const TrustSection: React.FC = () => {
  const { t, path } = useI18n();

  const pillars = [
    { icon: MapPin, title: t('home.trustAddressTitle'), desc: t('home.trustAddressDesc') },
    { icon: Lock, title: t('home.trustPrivacyTitle'), desc: t('home.trustPrivacyDesc') },
    { icon: CreditCard, title: t('home.trustPaymentTitle'), desc: t('home.trustPaymentDesc') },
    { icon: FileCheck2, title: t('home.trustVerifyTitle'), desc: t('home.trustVerifyDesc') },
    { icon: ShieldCheck, title: t('home.trustSecurityTitle'), desc: t('home.trustSecurityDesc') },
    { icon: Languages, title: t('home.trustBilingualTitle'), desc: t('home.trustBilingualDesc') },
  ];

  return (
    <section className="bg-brand-tint py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-brand-blue ring-1 ring-blue-200">
            <ShieldCheck className="h-3.5 w-3.5" /> {t('home.trustBadge')}
          </span>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {t('home.trustTitle')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600">{t('home.trustSub')}</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p) => (
            <div
              key={p.title}
              className="rounded-2xl border border-white bg-white/85 p-6 shadow-sm backdrop-blur transition-all duration-200 hover:-translate-y-1 hover:shadow-card"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-navy text-white">
                <p.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-bold text-slate-900">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
            </div>
          ))}
        </div>
        {/* ------------------------------------------------------------------
            The "Operated by" identity block that used to sit here (company
            name, street address, email) has MOVED TO THE FOOTER, next to the
            rest of the company and legal information. It was interrupting the
            flow between two marketplace sections, and in the footer it now
            appears on every page instead of only the home page — which is
            strictly better for crawlers and reputation scanners.

            What stays here are the two actions a visitor may want after
            reading the trust pillars. -------------------------------------- */}
        <div className="mt-10 flex flex-wrap gap-2">
          <Link
            to={path('/about')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition-colors hover:bg-slate-50"
          >
            {t('footer.aboutUs')}
          </Link>
          <Link
            to={path('/contact')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800"
          >
            {t('footer.contactUs')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default TrustSection;
